import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { CLASSIFICATION_CLASS, CLASSIFICATION_LABEL } from '@/lib/format';
import { Badge, BandBadge } from '@/components/Badge';
import { GaugeChart } from '@/components/GaugeChart';
import { FullPageSpinner } from '@/components/Skeleton';
import { useToast } from '@/components/Toast';
import type { AttemptResult } from '@/lib/types';

/**
 * FE-4 — the results screen.
 *
 * The defining constraint (FE-4-26 / §10.1): **this page does not reveal the correct
 * answer.** §14.4 measured that keyword matching alone already identifies the
 * In-Charge option in 135 of 200 questions; showing the answer would close the
 * remaining third and turn the instrument into a memory test within a week.
 *
 * Instead, items answered In-Control show the question's `coaching_note` — a
 * principle-level prompt that is useful without being a lookup table.
 */
export function ResultsPage() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  // The submit response is handed over via navigation state, so arriving straight from
  // the quiz needs no second round trip. A refresh or a deep link falls back to a fetch.
  const preloaded = (location.state as { result?: AttemptResult } | null)?.result;

  const query = useQuery({
    queryKey: ['result', attemptId],
    queryFn: () => api<AttemptResult>(`/me/attempts/${attemptId}/result`),
    enabled: Boolean(attemptId) && !preloaded,
    initialData: preloaded,
  });

  // FE-4-27 — the success toast fires once, on arrival from a submission.
  useEffect(() => {
    if (preloaded) toast.success('Check-in recorded.');
    // Intentionally keyed on the attempt only: re-running on every toast identity
    // change would fire it repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  if (query.isLoading) return <FullPageSpinner label="Loading your result…" />;

  const result = query.data;
  if (!result) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="card max-w-md p-7 text-center">
          <h1 className="text-base font-semibold text-ink">We couldn&rsquo;t find that check-in</h1>
          <Link to="/dashboard" className="btn-primary mt-5">
            Back to dashboard
          </Link>
        </div>
      </div>
    );
  }

  const inControlCount = result.breakdown.filter((b) => b.classification === 'IN_CONTROL').length;
  const naCount = result.breakdown.filter((b) => b.classification === 'NOT_APPLICABLE').length;

  return (
    <div className="min-h-screen bg-canvas">
      <main id="main" className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <p className="font-mono tnum text-xs uppercase tracking-wider text-muted">
          Quiz #{result.quiz.sequenceNo}
        </p>
        <h1 className="mt-1 text-xl font-semibold text-ink sm:text-2xl">Today&rsquo;s result</h1>

        {/* ── headline ── */}
        <section className="card mt-5 flex flex-col items-center gap-6 p-6 sm:flex-row sm:items-center sm:gap-8">
          <div className="shrink-0">
            {/* Same needle-behind-label treatment as the dashboard gauge. */}
            <GaugeChart
              value={result.inChargePct}
              label={result.isPartialDay ? 'Partial day' : "Today's score"}
              bandLabel={result.resultBandLabel}
              bandLow={40}
              bandHigh={70}
            />
          </div>

          <div className="min-w-0 flex-1 text-center sm:text-left">
            {/* R3 — "n of m applicable", because m is not always 10. */}
            <p className="text-base leading-relaxed text-ink">
              You answered{' '}
              <span className="font-mono tnum font-semibold">
                {result.score} of {result.applicableCount}
              </span>{' '}
              applicable {result.applicableCount === 1 ? 'question' : 'questions'} the In-Charge way.
            </p>

            {result.rollingAvg !== null && !result.isPartialDay && (
              <p className="mt-2 text-sm text-muted">
                Your rolling average is now{' '}
                <span className="font-mono tnum font-semibold text-ink">{result.rollingAvg}%</span>.
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
              <BandBadge band={result.resultBand} />
              {result.currentStreak > 0 && (
                <Badge className="border-line bg-surface-raised text-muted">
                  {result.currentStreak}-day streak
                </Badge>
              )}
              {naCount > 0 && (
                <Badge className="border-line bg-surface-raised text-muted">
                  {naCount} didn&rsquo;t apply
                </Badge>
              )}
            </div>

            {/* R3 — a partial day needs explaining, or it looks like a bug. */}
            {result.isPartialDay && (
              <p className="mt-4 rounded-control border border-secondary/35 bg-secondary-soft px-3.5 py-2.5 text-xs leading-relaxed text-ink">
                Too few questions applied today for this to count towards your trend or averages.
                Your streak is safe.
              </p>
            )}
          </div>
        </section>

        {/* ── per-question breakdown ── */}
        <section className="mt-6" aria-labelledby="breakdown-heading">
          <div className="mb-3 flex items-baseline justify-between gap-3">
            <h2 id="breakdown-heading" className="text-sm font-semibold text-ink">
              Your answers
            </h2>
            {inControlCount > 0 && (
              <span className="text-xs text-muted">
                {inControlCount} to reflect on
              </span>
            )}
          </div>

          <ol className="space-y-3">
            {result.breakdown.map((item) => (
              <li key={item.questionId} className="card p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm leading-relaxed text-ink">
                    <span className="mr-2 font-mono tnum text-muted">{item.position}.</span>
                    {item.text}
                  </p>
                  <Badge className={clsx('shrink-0', CLASSIFICATION_CLASS[item.classification])}>
                    {CLASSIFICATION_LABEL[item.classification]}
                  </Badge>
                </div>

                {item.chosenOptionText ? (
                  <p className="mt-3 rounded-control bg-surface-raised px-3.5 py-2.5 text-sm leading-relaxed text-muted">
                    <span className="mr-1.5 text-xs uppercase tracking-wider">You chose</span>
                    <br />
                    {item.chosenOptionText}
                  </p>
                ) : (
                  <p className="mt-3 text-sm italic text-muted">
                    You marked this as not applicable today.
                  </p>
                )}

                {/*
                  FE-4-26 — a coaching prompt on In-Control items only. Never the
                  correct option, and never shown on items already answered In-Charge.
                */}
                {item.coachingNote && (
                  <div className="mt-3 border-l-2 border-accent/60 pl-3.5">
                    <p className="text-xs font-semibold uppercase tracking-wider text-accent">
                      Something to consider
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted">{item.coachingNote}</p>
                  </div>
                )}
              </li>
            ))}
          </ol>
        </section>

        <div className="mt-7 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn-primary flex-1"
          >
            Back to dashboard
          </button>
          <Link to="/history" className="btn-secondary flex-1">
            See all check-ins
          </Link>
        </div>
      </main>
    </div>
  );
}
